<template>
  <div class="container" id="lifetime-success">
    <div class="text text--common">
      <div v-if="state === 'loading'">
        <p>{{ $t('lifetime_success_page.loading') }}</p>
      </div>

      <div v-else-if="state === 'success'">
        <h3>{{ $t('lifetime_success_page.success_title') }}</h3>
        <p>{{ $t('lifetime_success_page.success_description') }}</p>
      </div>

      <div v-else-if="state === 'pending'">
        <h3>{{ $t('lifetime_success_page.pending_title') }}</h3>
        <p>{{ $t('lifetime_success_page.pending_description') }}</p>
      </div>

      <div v-else-if="state === 'login'">
        <h3>{{ $t('lifetime_success_page.login_title') }}</h3>
        <p>{{ $t('lifetime_success_page.login_description') }}</p>
      </div>

      <div v-else>
        <h3>{{ $t('lifetime_success_page.error_title') }}</h3>
        <p v-if="api_error">{{ api_error }}</p>
        <p>{{ $t('lifetime_success_page.error_description') }}</p>
      </div>

      <div v-if="state === 'pending' || state === 'error'" class="lifetime-success__support">
        <p>
          <a :href="supportMailto">{{ $t('lifetime_success_page.contact_support', [support_email]) }}</a>
        </p>
        <p v-if="session_id">{{ $t('lifetime_success_page.reference') }} {{ session_id }}</p>
      </div>
    </div>

    <div class="lifetime-success__actions">
      <router-link v-if="state === 'login'" :to="loginRoute" class="btn btn--gold">
        <span class="btn__label">
          {{ $t('dashboard.login') }}
        </span>
      </router-link>

      <div v-if="(state === 'pending' || state === 'error') && session_id" v-on:click="validate()" class="btn btn--gold">
        <span class="btn__label">
          {{ $t('lifetime_success_page.check_again') }}
        </span>
      </div>

      <router-link v-if="state !== 'loading' && state !== 'login'" to="/" class="btn" :class="state === 'success' ? 'btn--gold' : 'btn--blue'">
        <span class="btn__label">
          {{ $t('lifetime_success_page.back_to_account') }}
        </span>
      </router-link>
    </div>
  </div>
</template>

<script>

import { SUPPORT_EMAIL, restoreCheckoutLocale } from '../config/lifetime';

// Retour automatique à l'accueil du compte après l'activation
const HOME_DELAY_MS = 6000;

// Page de retour de Stripe Checkout après le paiement de l'accès à vie (success_url de la session créée par l'API :
// <tableau de bord>#/lifetime/success?session_id={CHECKOUT_SESSION_ID}). Le webhook Stripe active l'accès de son côté ;
// ici on envoie l'identifiant de la session à validate-subscriptions pour activer sans attendre le webhook.
export default {
  data () {
    return {
      state: 'loading', // loading, success, pending, login, error
      session_id: null,
      api_error: null,
      support_email: SUPPORT_EMAIL,
      home_timer: null
    }
  },
  computed: {
    supportMailto: function () {
      let body = '';
      if (this.session_id) {
        body = this.$t('lifetime_success_page.reference') + ' ' + this.session_id;
      }
      return 'mailto:' + SUPPORT_EMAIL +
        '?subject=' + encodeURIComponent(this.$t('lifetime_success_page.support_subject')) +
        '&body=' + encodeURIComponent(body);
    },
    // Après la connexion, Login.vue ramène ici (paramètre redirect) pour finir l'activation
    loginRoute: function () {
      return { path: '/login', query: { redirect: this.$route.fullPath } };
    }
  },
  methods: {
    readSessionId: function () {
      let id = this.$route.query.session_id;
      if (!id) {
        // identifiant placé avant le # : https://app.odysound.com/fr/dashboard?session_id=…#/lifetime/success
        const match = window.location.search.match(/[?&]session_id=([^&]+)/);
        if (match) {
          try {
            id = decodeURIComponent(match[1]);
          } catch (e) {
            id = null;
          }
        }
      }
      return typeof id === 'string' && id.length > 0 ? id : null;
    },
    validate: function () {
      clearTimeout(this.home_timer);
      this.state = 'loading';
      this.api_error = null;

      this.$http.post(API_URL + '/validate-subscriptions', { stripe_checkout_session_id: this.session_id }, {
          responseType: "json",
          headers: {
            Authorization: 'Basic ' + this.$root.$data.token
          }
        })
        .then((response) => {
          const subscriptions = Array.isArray(response.body) ? response.body : [];
          const now = new Date();
          const active = subscriptions.some(function (s) {
            return s.lifetime === true && new Date(s.expire_date) > now;
          });
          if (active) {
            this.state = 'success';
            this.home_timer = setTimeout(() => { this.$router.replace('/'); }, HOME_DELAY_MS);
          } else {
            // paiement pas encore confirmé par Stripe (moyen de paiement différé) ou session pas encore rattachée
            this.state = 'pending';
          }
        }, (response) => {
          if (response.status === 401) {
            this.state = 'login';
            return;
          }
          this.state = 'error';
          this.api_error = this.apiErrorMessage(response);
        })
        .catch(() => {
          this.state = 'error';
        })
    },
    apiErrorMessage: function (response) {
      const message = response && response.body && response.body.message;
      if (message && typeof message[this.$i18n.locale] === 'string') {
        return message[this.$i18n.locale];
      }
      return null;
    }
  },
  mounted () {
    this.session_id = this.readSessionId();

    // Stripe revient sur le tableau de bord en français : on remet le client dans la langue où il a commencé
    const hash = this.session_id ? '#/lifetime/success?session_id=' + encodeURIComponent(this.session_id) : null;
    if (restoreCheckoutLocale(hash)) {
      return;
    }

    if (!localStorage.getItem('user-token')) {
      this.state = 'login';
      return;
    }
    this.$root.$data.token = localStorage.getItem('user-token');

    if (!this.session_id) {
      this.state = 'error';
      return;
    }
    this.validate();
  },
  beforeDestroy () {
    clearTimeout(this.home_timer);
  }
}
</script>
